import { type Component, createEffect, createResource, createSignal, Show } from 'solid-js'
import { z } from 'zod'
import Alert from '~/components/Alert'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Card from '~/components/Card'
import Form from '~/components/Form'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Switch from '~/components/Switch'
import { useToast } from '~/components/useToast'
import { useForm } from '~/hooks/useForm'
import { usePermissions } from '~/hooks/usePermissions'
import { auditService } from '~/services/audit.service'
import { resolveError } from '~/utils/error'

/**
 * Configuración de la auditoría: interruptor de captura, conexión a ClickHouse
 * y estado del pipeline.
 *
 * El root alterna entre la config del **sistema** (la conexión real y el
 * interruptor maestro) y la de **la empresa activa** (visibilidad y, si quiere,
 * un endpoint propio al que se hace fan-out).
 */
const TabConfiguracion: Component = () => {
  const toast = useToast()
  const { isRoot } = usePermissions()
  const [scope, setScope] = createSignal<'company' | 'system'>('company')

  const [config, { refetch: refetchConfig }] = createResource(
    () => scope(),
    async s => {
      try {
        return await auditService.getConfig(s === 'system' ? 'system' : undefined)
      } catch {
        return null
      }
    }
  )

  const [health, { refetch: refetchHealth }] = createResource(async () => {
    try {
      return await auditService.getHealth()
    } catch {
      return null
    }
  })

  const auditSchema = z.object({
    enabled: z.boolean().default(true),
    clickhouseUrl: z.string().trim().default(''),
    clickhouseUser: z.string().trim().default(''),
    clickhousePassword: z.string().default(''),
    clickhouseDb: z.string().trim().default('audit'),
    alertEmail: z.union([z.literal(''), z.string().email('Correo inválido')]).default(''),
    batchSize: z.union([z.literal(''), z.coerce.number().int().min(1)]).default(''),
    maxAttempts: z.union([z.literal(''), z.coerce.number().int().min(1)]).default('')
  })

  const form = useForm({
    schema: auditSchema,
    initialValues: () => {
      const c = config()
      if (!c) return { enabled: true, clickhouseUrl: '', clickhouseUser: '', clickhousePassword: '', clickhouseDb: 'audit', alertEmail: '', batchSize: '', maxAttempts: '' }
      const isCompanyScope = scope() === 'company'
      return {
        enabled: c.visible ?? c.captureEnabled ?? true,
        // En ámbito empresa, solo mostrar la URL propia, no el fallback del sistema.
        clickhouseUrl: isCompanyScope && !c.companyEndpointConfigured ? '' : (c.clickhouseUrl ?? ''),
        clickhouseUser: isCompanyScope && !c.companyEndpointConfigured ? '' : (c.clickhouseUser ?? ''),
        // La contraseña viene enmascarada (***); solo se envía si se cambia.
        clickhousePassword: '',
        clickhouseDb: c.clickhouseDb ?? 'audit',
        alertEmail: c.alertEmail ?? '',
        batchSize: c.batchSize ? String(c.batchSize) : '',
        maxAttempts: c.maxAttempts ? String(c.maxAttempts) : ''
      }
    },
    onSubmit: async values => {
      const isSystemScope = scope() === 'system'
      const payload: Record<string, unknown> = {
        scope: isSystemScope ? 'system' : 'company',
        enabled: Boolean(values.enabled),
        clickhouseUrl: (values.clickhouseUrl as string)?.trim() || null,
        clickhouseUser: (values.clickhouseUser as string)?.trim() || null,
        clickhouseDb: (values.clickhouseDb as string)?.trim() || 'audit',
        alertEmail: (values.alertEmail as string)?.trim() || null
      }
      const pwd = String(values.clickhousePassword ?? '').trim()
      if (pwd) payload.clickhousePassword = pwd
      if (values.batchSize !== '' && values.batchSize !== null) payload.batchSize = Number(values.batchSize)
      if (values.maxAttempts !== '' && values.maxAttempts !== null) payload.maxAttempts = Number(values.maxAttempts)

      await auditService.saveConfig(payload as never)
      toast.addToast({ title: 'Configuración de auditoría guardada', type: 'success' })
      await Promise.all([refetchConfig(), refetchHealth()])
    },
    onError: err => toast.addToast({ title: resolveError(err), type: 'danger' })
  })

  createEffect(() => {
    if (config()) form.reset()
  })

  const isSystem = () => scope() === 'system'

  return (
    <>
      <Show when={isRoot()}>
        <div class="mb-4 flex justify-end">
          <ButtonGroup size="sm" variant="soft">
            <Button color={scope() === 'company' ? 'primary' : 'default'} onClick={() => setScope('company')}>
              Esta empresa
            </Button>
            <Button color={scope() === 'system' ? 'primary' : 'default'} onClick={() => setScope('system')}>
              Sistema
            </Button>
          </ButtonGroup>
        </div>
      </Show>

      <Show when={isSystem()}>
        <Alert variant="info" class="mb-4">
          Estás editando la configuración del <strong>sistema</strong>: la conexión a ClickHouse y si la captura está activa. Las empresas sin config propia heredan de aquí.
        </Alert>
      </Show>

      {/* Estado del pipeline */}
      <Card>
        <Card.Title>
          <div class="flex items-center">
            <Icon icon="material-symbols-light:monitoring-outline" size={20} class="mr-2" />
            <h2 class="text-lg font-semibold">Estado del pipeline</h2>
          </div>
        </Card.Title>
        <Card.Body>
          <Show when={health.loading}>
            <p class="text-sm text-foreground-subtle">Cargando estado...</p>
          </Show>
          <Show when={health()}>
            {h => (
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p class="text-foreground-subtle">Pipeline</p>
                  <p class={`font-medium ${h().active ? 'text-success' : 'text-warning'}`}>{h().active ? 'Activo' : 'Parado'}</p>
                </div>
                <div>
                  <p class="text-foreground-subtle">Estado</p>
                  <p class="font-medium capitalize">{h().status}</p>
                </div>
                <div>
                  <p class="text-foreground-subtle">Pendientes en outbox</p>
                  <p class="font-medium">{h().pendingOutbox}</p>
                </div>
                <div>
                  <p class="text-foreground-subtle">Fallos consecutivos</p>
                  <p class="font-medium">
                    {h().consecutiveFailures} / {h().threshold}
                  </p>
                </div>
                <Show when={h().lastError}>
                  <div class="col-span-2 md:col-span-4">
                    <p class="text-foreground-subtle">Último error</p>
                    <p class="font-mono text-xs text-danger break-all">{h().lastError}</p>
                  </div>
                </Show>
              </div>
            )}
          </Show>
          <div class="mt-3">
            <Button size="sm" variant="soft" color="default" onClick={() => refetchHealth()} prependIcon="material-symbols-light:refresh-outline">
              Refrescar estado
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Formulario de configuración */}
      <Card class="mt-4">
        <Card.Title>
          <div class="flex items-center">
            <Icon icon="material-symbols-light:settings-outline" size={20} class="mr-2" />
            <h2 class="text-lg font-semibold">{isSystem() ? 'Conexión del sistema' : 'Configuración de esta empresa'}</h2>
          </div>
        </Card.Title>
        <Card.Body>
          <Show when={config.loading}>
            <p class="text-sm text-foreground-subtle">Cargando configuración...</p>
          </Show>
          <Show when={!config.loading}>
            <Form id="audit-form" controller={form}>
              <div class="space-y-4">
                <Switch
                  label={isSystem() ? 'Captura activa' : 'Mostrar auditoría en esta empresa'}
                  helperText={
                    isSystem() ? 'Si se desactiva, no se capturan eventos (ni siquiera al outbox).' : 'Si se desactiva, la empresa no ve su auditoría (pero se sigue guardando en el sistema).'
                  }
                  checked={Boolean(form.values().enabled)}
                  onChange={e => form.setValue('enabled', e.currentTarget.checked)}
                />

                <div class="rounded-lg border border-border p-4 space-y-4">
                  <h3 class="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Icon icon="material-symbols-light:database-outline" size={18} />
                    ClickHouse {isSystem() ? '(sistema)' : '(endpoint propio - fan-out)'}
                  </h3>
                  <Show when={!isSystem()}>
                    <Alert variant="info">
                      Si tu empresa configura su propio ClickHouse, los eventos se guardan <strong>siempre</strong> en el sistema y <strong>además</strong> en el tuyo.
                    </Alert>
                  </Show>

                  <Input name="clickhouseUrl" label="URL de ClickHouse" placeholder="http://localhost:8123" />
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input name="clickhouseUser" label="Usuario" placeholder="default" />
                    <Input name="clickhousePassword" label="Contraseña" type="password" placeholder={config()?.clickhousePassword ? '•••••••• (se conserva si se deja vacío)' : 'Vacía si no tiene'} />
                  </div>
                  <Input name="clickhouseDb" label="Base de datos" placeholder="audit" />
                </div>

                <Input name="alertEmail" label="Email de alerta (caída de ClickHouse)" placeholder="ops@empresa.com" helperText="Se avisa una sola vez por caída y al recuperarse." />

                <details class="rounded-lg border border-border p-3">
                  <summary class="cursor-pointer text-sm font-medium text-foreground">Avanzado</summary>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <Input name="batchSize" label="Tamaño de lote" type="number" placeholder="100" />
                    <Input name="maxAttempts" label="Reintentos máximos" type="number" placeholder="10" />
                  </div>
                  <p class="text-xs text-foreground-subtle mt-2">Vacío = valor por defecto del sistema. Los cambios se aplican al reiniciar el pipeline.</p>
                </details>
              </div>
            </Form>

            <div class="flex justify-end gap-2 mt-4">
              <Button type="submit" form="audit-form" disabled={form.submitting() || !form.valid()} color="primary">
                {form.submitting() ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>

            <Show when={config()?.clickhouseUrl && config()?.clickhousePassword?.includes('***')}>
              <p class="text-xs text-foreground-subtle mt-2">La contraseña se muestra enmascarada. Deja el campo vacío para conservar la actual.</p>
            </Show>
          </Show>
        </Card.Body>
      </Card>
    </>
  )
}

export default TabConfiguracion
